/**
 * LessonEndReflection — calm inline post-lesson reflection rail.
 *
 * B0 Step 5 — Merge C1 + B2 into B1 (Goal Outcome Loop). This rail is
 * the single canonical learner-facing post-lesson surface, replacing:
 *
 *   • The standalone `/confidence-check/:moduleId` quiz route (C1 fold).
 *   • The auto-opening `PostLessonReflection` modal (B2 fold).
 *
 * Doctrine (Patch Decisions Card §B1/§B2/§C1):
 *   - One connected operational loop, not multiple disconnected
 *     reflection systems.
 *   - Calm 3-outcome rating mirrors the B1 verdict vocabulary
 *     ("clear" / "partly" / "not yet") so instructor and learner
 *     surfaces speak the same language.
 *   - Optional free-text "anything else to add?" expands inline.
 *   - No modal, no popover, no gamification, no celebration. The
 *     lesson page is the surface — the reflection is part of it.
 *   - Historical schema preserved: POSTs to the existing
 *     `/api/reflection` endpoint so the `lesson_reflections`
 *     collection continues to receive entries.
 *
 * Tone rules:
 *   clarity without blame · progress without shame ·
 *   positive next action  · confidence through proof
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, ChevronDown, ChevronUp, Sparkles } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// B1 vocabulary mirror — same three outcome branches the Goal Outcome
// Loop uses on the instructor side. Keeps both sides of the lesson
// using the same words.
const OUTCOMES = [
  {
    id: "clear",
    label: "Clear",
    description: "Today landed — the routine held.",
  },
  {
    id: "partly",
    label: "Partly",
    description: "Some of it stuck; a piece carries forward.",
  },
  {
    id: "not_yet",
    label: "Not yet",
    description: "More repetitions before this one settles.",
  },
];

export default function LessonEndReflection({ lessonId, onCaptured }) {
  const [outcome, setOutcome] = useState(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  if (!lessonId) return null;

  const submit = async () => {
    if (!outcome || busy) return;
    setBusy(true);
    try {
      // B0 Step 5 — `outcome` carries the canonical B1 verdict
      // ("clear" / "partly" / "not_yet"). `adjustment` carries the
      // optional free-text note (B2 fold — same data store, single
      // surface). Legacy `feeling` / `mindset` / `factor` / `accuracy`
      // fields default to empty strings server-side, preserving the
      // historical schema for the ~80+ existing rows.
      await axios.post(
        `${API_URL}/api/reflection`,
        {
          lesson_id: lessonId,
          outcome,
          adjustment: note.trim() || "",
        },
        { withCredentials: true },
      );
      setSaved(true);
      onCaptured?.({ outcome, note: note.trim() || null });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string" && detail.toLowerCase().includes("already")) {
        // Idempotent UX — already reflected for this lesson is fine.
        setSaved(true);
        onCaptured?.({ outcome, note: note.trim() || null, alreadyReflected: true });
      } else {
        toast.error("Couldn't save the reflection just yet.");
      }
    } finally {
      setBusy(false);
    }
  };

  if (saved) {
    return (
      <div
        data-testid="lesson-end-reflection-saved"
        className="mt-6 rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 p-4"
      >
        <div className="flex items-center gap-2">
          <Check className="w-4 h-4 text-emerald-600" />
          <p className="text-sm font-bold text-slate-900 dark:text-white">
            Reflection captured.
          </p>
        </div>
        <p className="text-[12px] text-slate-600 dark:text-slate-300 mt-1 leading-snug">
          Three hubs, one connected journey — your next lesson picks up where this one ended.
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="lesson-end-reflection"
      className="mt-6 rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 p-4"
    >
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-4 h-4 text-slate-500" />
        <p className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500">
          How did this lesson land?
        </p>
      </div>
      <p className="text-[12px] text-slate-500 dark:text-slate-400 mb-3 leading-snug">
        One calm rating. No grading.
      </p>

      <div className="grid grid-cols-3 gap-2">
        {OUTCOMES.map((o) => {
          const active = outcome === o.id;
          return (
            <button
              key={o.id}
              type="button"
              data-testid={`lesson-end-outcome-${o.id}`}
              onClick={() => setOutcome(o.id)}
              className={`text-left rounded-xl px-3 py-2.5 border text-[12px] font-medium transition ${
                active
                  ? "border-slate-900 dark:border-white bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                  : "border-slate-200 dark:border-slate-700 bg-white/40 dark:bg-slate-900/40 text-slate-600 dark:text-slate-300 hover:bg-white"
              }`}
            >
              <span className="block text-sm font-extrabold">{o.label}</span>
              <span className="block text-[11px] mt-0.5 opacity-80 leading-snug">
                {o.description}
              </span>
            </button>
          );
        })}
      </div>

      <button
        type="button"
        data-testid="lesson-end-note-toggle"
        onClick={() => setNoteOpen((v) => !v)}
        className="mt-3 inline-flex items-center gap-1 text-[12px] font-medium text-slate-600 dark:text-slate-300 underline-offset-2 hover:underline"
      >
        {noteOpen ? (
          <ChevronUp className="w-3.5 h-3.5 opacity-70" />
        ) : (
          <ChevronDown className="w-3.5 h-3.5 opacity-70" />
        )}
        Anything else to add? (optional)
      </button>

      {noteOpen ? (
        <textarea
          data-testid="lesson-end-note"
          value={note}
          onChange={(e) => setNote(e.target.value.slice(0, 500))}
          placeholder="e.g. The roundabout still felt rushed — want to repeat the slow approach next time."
          rows={3}
          className="mt-2 w-full text-[13px] rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-slate-900 dark:text-white resize-none"
        />
      ) : null}

      <button
        type="button"
        onClick={submit}
        data-testid="lesson-end-reflection-submit"
        disabled={!outcome || busy}
        className="mt-3 w-full inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-slate-900 hover:bg-slate-800 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed text-white font-extrabold text-sm transition-colors"
      >
        {busy ? "Saving…" : "Capture this reflection"}
      </button>
    </div>
  );
}
